import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useCountryContext } from '../../../contexts/CountryProvider';
import useCountryActions from '../../../hooks/APIHooks/users/useCountryActions';
import { CountryReqBody } from '../../../types/countries';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import SelectedCountryModalTable from '../../SetupSubPageComps/CountriesComps/SelectedCountryModalTable';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaRestCountrySelect from '../Inputs/AcademiaRestCountrySelect';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AddCountryModal = ({
  handleModalClose,
}: {
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [country, setCountry] = useState<CountryReqBody | null>(null);
  const [resetCountry, setResetCountry] = useState<boolean>(false);

  // integration of context hooks here
  const { selectedCountries, setSelectedCountries } = useCountryContext();
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { addNewCountryHandler } = useCountryActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // adding country to selected list when country is selected here
  useEffect(() => {
    if (country) {
      setSelectedCountries((state) => [...state, country]);

      setCountry(null);
      setResetCountry(true);
    }
  }, [country, setSelectedCountries]);

  // handler function to add new country declared here
  const addCountryHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      const body: Array<CountryReqBody> = selectedCountries.map((country) => ({
        ...country,
        created_by: currentUser?.username,
      }));

      await addNewCountryHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Countries Added!!');

            handleModalClose();
            setSelectedCountries([]);
            queryClient.invalidateQueries({ queryKey: ['country-list'] });
          } else {
            toast.error(res.message || 'Countries Adding Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering add country modal component here
  return (
    <ParentModal
      onClick={() => {
        handleModalClose();
        setSelectedCountries([]);
      }}
    >
      <ModalTitle titleText='add country' />

      <Formik initialValues={{ country: '' }} onSubmit={addCountryHandler}>
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaRestCountrySelect
              name='country'
              label='Select Country'
              setCountry={setCountry}
              resetCountry={resetCountry}
              setResetCountry={setResetCountry}
              selectedCountries={selectedCountries}
              isRequired
            />

            {selectedCountries.length ? <SelectedCountryModalTable /> : null}

            <AcademiaButton
              title={
                !selectedCountries.length
                  ? 'Please, select some countries first!'
                  : ''
              }
              type='submit'
              variant='premium'
              icon={<TbSquareRoundedPlusFilled className='size-6' />}
              isDisabled={!selectedCountries.length}
              isLoading={props.isSubmitting}
              fullWidth
            >
              Add Country
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default AddCountryModal;
